import { memo } from 'react';
import { Button, Card, Col, Row } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { CATEGORY_LINKS, IMAGE_PROXY_URL, OTHER_CATEGORIES } from '../../utils/constant';
import { styles } from '../../styles';
import { getTimer } from '../../utils/timer';

const cardStyle = {
  border: '1px solid rgba(0, 0, 0, 0.1)',
  borderRadius: '8px',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  height: '100%',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-start',
};

const iconStyle: React.CSSProperties = {
  color: '#000000',
  marginRight: '10px',
  fontSize: '21px',
  strokeWidth: '15px',
  alignItems: 'middle',
};

const liveTimerStyle: React.CSSProperties = { color: 'red' };

export function fixtureCardKey(fixture: any, categoryName?: string): string {
  return [
    categoryName || fixture?.category || '',
    fixture?.teamA || '',
    fixture?.teamB || '',
    fixture?.matchDate || fixture?.matchTime || 'highlights',
  ].join('|');
}

const Timer = memo(function Timer({ timerValue }: { timerValue: string }) {
  return (
    <strong style={timerValue === 'Live' ? liveTimerStyle : undefined}>
      {timerValue}
    </strong>
  );
});

const YoutubeHighlightsLink = memo(function YoutubeHighlightsLink() {
  return (
    <Button type="primary" className="watch-highlights-btn">
      ▶ Highlights
    </Button>
  );
});

const CardContent = memo(function CardContent({
  fixture,
  isDesktop,
}: {
  fixture: any;
  isDesktop: boolean;
}) {
  const categoryLabel = !OTHER_CATEGORIES.includes(fixture?.category)
    ? 'Soccer'
    : String(fixture?.category || '').toUpperCase();

  return (
    <div className="fixture-card">
      <div className="fixture-link">
        <div className="flex-display">
          {isDesktop && <p className="category">{categoryLabel} </p>}
          {isDesktop && <span className="dash" />}
          <p className="team-name">
            <span>
              {!isDesktop && <strong>{fixture.teamA}</strong>}
              <img
                className="fixture-image"
                src={`${IMAGE_PROXY_URL}${fixture.teamAImage}`}
                alt={`${fixture?.category || fixture?.subCategoryName || 'Team'} - ${fixture.teamA}`}
                width={20}
                height={20}
                loading="lazy"
                decoding="async"
              />
            </span>
            {isDesktop && <strong>{fixture.teamA}</strong>}
          </p>
          {fixture?.teamB ? <p className="verses">vs</p> : null}
          {fixture?.teamB && (
            <p className="team-name" style={{ margin: '0px' }}>
              <strong>{fixture.teamB}</strong>{' '}
              <span>
                <img
                  className="fixture-image"
                  src={`${IMAGE_PROXY_URL}${fixture.teamBImage}`}
                  alt={`${fixture?.category || fixture?.subCategoryName || 'Team'} - ${fixture.teamB}`}
                  width={20}
                  height={20}
                  loading="lazy"
                  decoding="async"
                />
              </span>
            </p>
          )}
        </div>
      </div>
      {fixture?.matchDate && (
        <p className="match-time">
          <Timer timerValue={getTimer(fixture)} />
        </p>
      )}
      {!fixture?.matchDate && <YoutubeHighlightsLink />}
    </div>
  );
});

export const FixtureCard = memo(function FixtureCard({
  fixture,
  isDesktop,
}: {
  fixture: any;
  isDesktop: boolean;
}) {
  let link = `/fixture/${fixture.teamA}-vs-${fixture.teamB}`;
  if (!fixture?.matchDate) {
    const home = fixture?.teamA ?? '';
    const away = fixture?.teamB ?? '';
    const league = fixture?.subCategoryName ?? fixture?.category ?? '';
    const query = encodeURIComponent(`${home} vs ${away} highlights ${league}`);
    link = `https://www.youtube.com/results?search_query=${query}`;
  }

  if (fixture?.matchDate) {
    return (
      <Link to={link}>
        <CardContent fixture={fixture} isDesktop={isDesktop} />
      </Link>
    );
  }

  return (
    <a href={link} target="_blank" rel="noopener noreferrer">
      <CardContent fixture={fixture} isDesktop={isDesktop} />
    </a>
  );
});

export const CategoryCard = memo(function CategoryCard({
  category,
  categoryName,
  isDesktop,
}: {
  category: any;
  categoryName?: string;
  isDesktop: boolean;
}) {
  if (!category?.games?.length) return null;

  const leagueKey = category.subCategoryName || 'league';
  const moreLink =
    !categoryName && CATEGORY_LINKS[category?.subCategoryName?.toUpperCase()]
      ? `/streams/${CATEGORY_LINKS[category.subCategoryName.toUpperCase()]}`
      : null;

  return (
    <Col style={{ marginBottom: '8px', justifyContent: 'center' }} span={24}>
      <Card
        size="small"
        title={
          <div style={headerStyle}>
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                color: '#000000',
                fontSize: '18px',
                textTransform: 'capitalize',
              }}
            >
              {category?.subCategoryImage ? (
                <img
                  style={{ width: '30px', height: '20px', paddingRight: '10px' }}
                  src={ `${IMAGE_PROXY_URL}${category.subCategoryImage}` }
                  alt={category.subCategoryName}
                  width={30}
                  height={20}
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <InfoCircleOutlined style={iconStyle} />
              )}
              {category?.subCategoryName?.toUpperCase()}
            </span>
          </div>
        }
        extra={
          moreLink ? (
            <Link to={moreLink} style={styles.more} className="more">
              {' '}
              More
            </Link>
          ) : null
        }
        style={cardStyle}
      >
        <Row gutter={24}>
          {category.games.map((fixture: any) => {
            const withCategory = {
              ...fixture,
              category: category.subCategoryName,
              subCategoryName: category.subCategoryName,
            };
            return (
              <Col
                span={12}
                key={fixtureCardKey(withCategory, leagueKey)}
                style={{ padding: '0px' }}
              >
                <FixtureCard fixture={withCategory} isDesktop={isDesktop} />
              </Col>
            );
          })}
        </Row>
      </Card>
    </Col>
  );
});
